import { useEffect, useState, type ReactNode } from 'react';
import { boxSubdomain } from '@lub0v/santa-utils';
import { BoxDomainUnavailable } from './BoxDomainUnavailable';
import { configuredBrowserBoxDomains, subdomainMainSiteUrl } from './boxDomainNavigation';

declare global {
  interface Window {
    __ssBoxDomains?: string[];
    __ssBoxKey?: string;
    __ssIsPreview?: boolean;
  }
}
export function currentBoxSubdomain(): string | null {
  if (typeof window === 'undefined') return null;
  const bases = configuredBrowserBoxDomains();
  return boxSubdomain(window.location.hostname, bases);
}

/** Resolve canonical (possibly mixed-case) keys before loading the ordinary game. */
export function BoxDomainBoundary({ children }: { children: ReactNode }) {
  const label = currentBoxSubdomain();
  const redirectUrl = subdomainMainSiteUrl();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>(
    label || redirectUrl ? 'loading' : 'ready',
  );
  useEffect(() => {
    if (!label) {
      if (redirectUrl) window.location.replace(redirectUrl);
      return;
    }
    const abort = new AbortController();
    delete window.__ssBoxKey;
    fetch('/api/box-domain', { credentials: 'include', signal: abort.signal })
      .then(async (response) => {
        if (response.status === 404) {
          if (!abort.signal.aborted) {
            if (redirectUrl) window.location.replace(redirectUrl);
            else setState('missing');
          }
          return;
        }
        if (!response.ok) throw new Error('Box unavailable');
        const data = await response.json();
        if (typeof data.key !== 'string') throw new Error('Invalid box');
        if (abort.signal.aborted) return;
        window.__ssBoxKey = data.key;
        setState('ready');
      })
      .catch(() => {
        if (!abort.signal.aborted) setState('error');
      });
    return () => abort.abort();
  }, [label, attempt, redirectUrl]);
  if (state === 'loading') return <main aria-busy="true" />;
  if (state === 'missing' || state === 'error') {
    return (
      <BoxDomainUnavailable
        missing={state === 'missing'}
        onRetry={() => {
          setState('loading');
          setAttempt((value) => value + 1);
        }}
      />
    );
  }
  return children;
}
